import { readableColor } from 'polished';
import { RepresentationData } from '@ardoq/data-model';
import { colors } from '@ardoq/design-tokens';
import { IconName } from '@ardoq/icons';
import { drawSpriteIcon } from 'tabview/blocks/visual/node/drawSpriteIcon';
import { paintImageInCircle } from 'tabview/canvasRendering/paintImageInCircle';
import { svgImage } from 'tabview/canvasRendering/canvasResolvedImages';
import { paintIcon } from 'tabview/canvasRendering/paintIcon';
import {
  MODERNIZED_BLOCK_DIAGRAM_CORNER_RADIUS as CORNER_RADIUS,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_SHAPE_HEIGHT as SHAPE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_CONDITIONAL_FORMATTING_INSET as CONDITIONAL_FORMATTING_INSET,
  MODERNIZED_BLOCK_DIAGRAM_CONDITIONAL_FORMATTING_STROKE_WIDTH as CONDITIONAL_FORMATTING_STROKE_WIDTH,
} from '../consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_COLLAPSED_COMPONENT_GROUP_STRIPE_HEIGHT as STRIPE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_IMAGE_CIRCLE_STROKE_WIDTH as CIRCLE_STROKE_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_ICON_SIZE as ICON_SIZE,
} from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';

type Bounds = { x: number; y: number; width: number; height: number };

const paintOutlineCircle = (
  context: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  fill: string | null,
  stroke: string | null
) => {
  const radius = SHAPE_HEIGHT / 2;
  context.beginPath();
  context.ellipse(cx, cy, radius, radius, 0, 0, 2 * Math.PI);
  if (fill) {
    context.fillStyle = fill;
    context.fill();
  }
  if (stroke) {
    const { lineWidth } = context;
    context.lineWidth = CIRCLE_STROKE_WIDTH;
    context.strokeStyle = stroke;
    context.stroke();
    context.lineWidth = lineWidth;
  }
};

type CollapsedComponentGroupArgs = {
  /** The component-type representation drawn centered in the body. */
  representation: RepresentationData;
  /** Resolved component-type color; null when the group has no color. */
  componentColor: string | null;
  /** Top of the representation shape relative to the node top (see collapsedComponentGroupShapeY). */
  shapeY: number;
  /** Expander chevron rect in world coords; null when the group is not collapsible. */
  expander: Bounds | null;
  isConditionallyFormatted: boolean;
  isTransparentized: boolean;
};

/**
 * Draws a collapsed component group as the canonical Modernized Block Diagram card: a grey
 * rounded body, a separator line under a header stripe, an UNFOLD_MORE expander top-left, and
 * the component-type representation (image / shape / icon) centered in the body. Pure canvas —
 * no yFiles/graph domain types — so the block diagram and the Assistant Diagram share it. The
 * main label is drawn by the caller (the block diagram uses a yFiles label; the assistant draws
 * it on the canvas), so it is intentionally not drawn here.
 */
export const drawCollapsedComponentGroup = (
  context: CanvasRenderingContext2D,
  { x, y, width: nodeWidth, height: nodeHeight }: Bounds,
  {
    representation,
    componentColor,
    shapeY,
    expander,
    isConditionallyFormatted,
    isTransparentized,
  }: CollapsedComponentGroupArgs
): void => {
  const { globalAlpha } = context;
  if (isTransparentized) {
    context.globalAlpha = 0.32;
  }

  // Grey rounded body.
  context.beginPath();
  context.roundRect(x, y, nodeWidth, nodeHeight, CORNER_RADIUS);
  context.fillStyle = colors.grey50;
  context.strokeStyle = colors.grey400;
  context.fill();
  context.stroke();

  // Conditional-formatting inset border.
  if (isConditionallyFormatted && componentColor) {
    const { lineWidth } = context;
    context.beginPath();
    context.roundRect(
      x + CONDITIONAL_FORMATTING_INSET,
      y + CONDITIONAL_FORMATTING_INSET,
      nodeWidth - 2 * CONDITIONAL_FORMATTING_INSET,
      nodeHeight - 2 * CONDITIONAL_FORMATTING_INSET,
      CORNER_RADIUS - CONDITIONAL_FORMATTING_INSET
    );
    context.lineWidth = CONDITIONAL_FORMATTING_STROKE_WIDTH;
    context.strokeStyle = componentColor;
    context.stroke();
    context.lineWidth = lineWidth;
  }

  // Expander chevron (top-left of the header).
  if (expander) {
    drawSpriteIcon(
      IconName.UNFOLD_MORE,
      expander.width,
      colors.grey950,
      expander.x + expander.width / 2,
      expander.y + expander.height / 2,
      context
    );
  }

  // Header separator line.
  context.beginPath();
  const separatorY = y + STRIPE_HEIGHT;
  context.moveTo(x, separatorY);
  context.lineTo(x + nodeWidth, separatorY);
  context.strokeStyle =
    isConditionallyFormatted && componentColor
      ? componentColor
      : colors.grey300;
  context.stroke();

  // Representation (image-in-circle / shape / icon-in-circle), centered via shapeY.
  const fill = componentColor || colors.transparent0;
  const {
    isImage,
    value: representationValue,
    shapeName,
    icon,
  } = representation;
  if (isImage && representationValue) {
    const cx = x + nodeWidth / 2;
    const radius = SHAPE_HEIGHT / 2;
    const cy = y + shapeY + radius;
    if (componentColor) {
      paintOutlineCircle(context, cx, cy, null, componentColor);
    }
    paintImageInCircle(context, representationValue, cx, cy, radius);
  } else if (shapeName) {
    context.drawImage(
      svgImage(
        `${shapeName}~~${colors.black950}~~${fill}~~${colors.transparent0}~~${nodeWidth}~~${SHAPE_HEIGHT}`
      ),
      x,
      y + shapeY
    );
  } else if (icon && representationValue) {
    const cx = x + nodeWidth / 2;
    const radius = SHAPE_HEIGHT / 2;
    const cy = y + shapeY + radius;
    paintOutlineCircle(context, cx, cy, fill, null);
    const iconColor = readableColor(fill, colors.grey950, colors.white950);
    paintIcon(context, cx, cy, ICON_SIZE, iconColor, icon);
  }

  context.globalAlpha = globalAlpha;
};
